import React from "react";
import Button from "../Button/Button";
import Loader from "../Loader/Loader";

export type SortOrder = "asc" | "desc";

export interface DataTableColumn<TRow = Record<string, unknown>> {
  key: string;
  label?: React.ReactNode;
  sortable?: boolean;
  render?: (row: TRow) => React.ReactNode;
}

export interface DataTableSort {
  key?: string;
  order?: SortOrder;
  onChange?: (key: string, order: SortOrder) => void;
}

export interface DataTablePagination {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<TRow = Record<string, unknown>> {
  columns: DataTableColumn<TRow>[];
  rows: TRow[];
  pagination?: DataTablePagination;
  sort?: DataTableSort;
  emptyMessage?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

function getCellContent<TRow extends Record<string, unknown>>(
  column: DataTableColumn<TRow>,
  row: TRow
): React.ReactNode {
  if (column.render) {
    return column.render(row);
  }

  return row[column.key] as React.ReactNode;
}

function getNextOrder(sort: DataTableSort | undefined, columnKey: string): SortOrder {
  if (!sort || sort.key !== columnKey) {
    return "asc";
  }

  return sort.order === "asc" ? "desc" : "asc";
}

function getPageCount(pagination?: DataTablePagination): number {
  if (!pagination || !pagination.perPage) {
    return 0;
  }

  return Math.max(1, Math.ceil(pagination.total / pagination.perPage));
}

function DataTable<TRow extends Record<string, unknown> = Record<string, unknown>>({
  columns,
  rows,
  pagination,
  sort,
  emptyMessage = "Aucun résultat",
  isLoading = false,
  className,
}: DataTableProps<TRow>) {
  const handleSort = (column: DataTableColumn<TRow>) => {
    if (!column.sortable || !sort || !sort.onChange) {
      return;
    }
    sort.onChange(column.key, getNextOrder(sort, column.key));
  };

  const renderSortIndicator = (column: DataTableColumn<TRow>) => {
    if (!column.sortable) {
      return null;
    }
    const isActive = !!sort && sort.key === column.key;
    const indicator = isActive && sort?.order === "desc" ? "▼" : "▲";
    return (
      <span className="ui-data-table__sort" aria-hidden="true">
        {indicator}
      </span>
    );
  };

  const renderHeaderCell = (column: DataTableColumn<TRow>) => {
    const isSorted = !!sort && sort.key === column.key;
    const ariaSort: "ascending" | "descending" | "none" = isSorted
      ? sort?.order === "desc"
        ? "descending"
        : "ascending"
      : "none";

    return (
      <th key={column.key} scope="col" aria-sort={column.sortable ? ariaSort : undefined}>
        {column.sortable ? (
          <button
            type="button"
            className="ui-data-table__sort-button"
            onClick={() => handleSort(column)}
          >
            <span>{column.label}</span>
            {renderSortIndicator(column)}
          </button>
        ) : (
          column.label
        )}
      </th>
    );
  };

  const renderPagination = () => {
    if (!pagination) {
      return null;
    }

    const pageCount = getPageCount(pagination);
    const page = pagination.page || 1;

    return (
      <div className="ui-data-table__pagination">
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => pagination.onPageChange(page - 1)}
        >
          Précédent
        </Button>
        <span className="ui-data-table__page">
          Page {page} / {pageCount}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= pageCount}
          onClick={() => pagination.onPageChange(page + 1)}
        >
          Suivant
        </Button>
      </div>
    );
  };

  const renderBody = () => {
    if (rows.length === 0) {
      return (
        <tr>
          <td className="ui-data-table__empty" colSpan={columns.length}>
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return rows.map((row, rowIndex) => {
      const key =
        (row as { id?: React.Key; key?: React.Key }).id ??
        (row as { id?: React.Key; key?: React.Key }).key ??
        rowIndex;
      return (
        <tr key={key}>
          {columns.map((column) => (
            <td key={column.key}>{getCellContent(column, row)}</td>
          ))}
        </tr>
      );
    });
  };

  const classes = ["ui-data-table", isLoading ? "ui-data-table--loading" : null, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <div className="ui-data-table__scroll">
        <table>
          <thead>
            <tr>{columns.map((column) => renderHeaderCell(column))}</tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>
        {isLoading ? (
          <div className="ui-data-table__loading">
            <Loader message="Chargement" />
          </div>
        ) : null}
      </div>
      {renderPagination()}
    </div>
  );
}

export default DataTable;
